/**
 * PersonaDayMatrix — the « Runs du <jour> » grid of §4.5.3 (mock: the `table.grid`).
 *
 * Rows are zones (zone × language, the persona's configuration), columns are engines,
 * one cell per configuration: « présence X % · gagné Y % » over the cell's runs, then
 * one square per run. A square is the only way into a run.
 *
 * Three cell states that must NOT collapse into « 0 % » (mock README):
 *   - the (zone, engine) pair is not configured → hatched « non suivi »
 *   - configured but this launch has no row for it → hatched « premiers runs le <date> »
 *   - terminal but the finalizer has not written `stats` + `runs[]` yet → spinner,
 *     re-read every 5 s (≤ 2 min). `isFinalized` is the readability gate.
 *
 * Tier 1 of the loading contract: ONE request per launch
 * (`…/launches/:launchId/matrix`), independent of the persona ROLLUP row — so this
 * panel still renders when the roll-up is missing.
 */

import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  cellPath as buildCellPath,
  cellKey as buildCellKey,
  type ConversationEngine,
  type ConversationPersona,
  type ConversationSubRun,
} from '@traaker/shared';
import { api } from '../../services/api';
import { EngineLogo } from '../brand/EngineLogo';
import { RunSquare, RunSquareLegend, squareDesc } from './RunSquare';
import { HATCH_SX, engineDisplay, engineLogoId, pct } from './personaFormat';

export const TERMINAL_STATUSES = new Set(['completed', 'partial', 'failed']);

/** A terminal cell is readable only once the finalizer wrote `stats` + the `runs[]` array. */
export function isFinalized(cell: ConversationSubRun | null): cell is ConversationSubRun {
  return !!cell && TERMINAL_STATUSES.has(cell.status)
    && (cell.status === 'failed' || (cell.stats != null && Array.isArray(cell.runs)));
}

const CELL_SX = {
  borderBottom: '1px solid',
  borderColor: 'divider',
  px: 1,
  py: 1,
  fontSize: '0.8125rem',
  textAlign: 'left' as const,
  verticalAlign: 'top' as const,
};
const HEAD_SX = {
  ...CELL_SX,
  fontSize: '0.6875rem',
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: 'text.secondary',
  whiteSpace: 'nowrap' as const,
};

export function PersonaDayMatrix({
  persona,
  brandId,
  launchId,
  refreshKey = 0,
  firstRunAt,
}: {
  persona: ConversationPersona;
  brandId: string;
  launchId: string;
  /** Bump to re-read the same launch (e.g. when it just finished). */
  refreshKey?: number;
  /** `persona.nextRunAt` — used for the « premiers runs le <date> » cell. */
  firstRunAt?: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<(ConversationSubRun | null)[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    if (refreshKey === 0) setLoading(true);
    const load = () => {
      api.get<{ persona: ConversationPersona; cells: (ConversationSubRun | null)[] }>(
        `/brands/${brandId}/personas/${persona.personaId}/launches/${launchId}/matrix`,
      ).then((result) => {
        if (cancelled) return;
        const next = result?.cells ?? [];
        setRows(next);
        setLoading(false);
        // The counter flips a cell terminal before the finalizer writes its stats:
        // keep re-reading (5 s, ≤ 2 min) while a terminal cell has no summary yet.
        const awaiting = next.some((c) => c && TERMINAL_STATUSES.has(c.status) && !isFinalized(c));
        if (awaiting && attempts++ < 24) timer = setTimeout(load, 5000);
      }).catch(() => {
        if (!cancelled) { setRows([]); setLoading(false); }
      });
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [persona.personaId, brandId, launchId, refreshKey]);

  /** cellKey → row. Built from the row's own engine/language/zone, never from index order. */
  const byKey = useMemo(() => {
    const m = new Map<string, ConversationSubRun>();
    rows.forEach((r, i) => {
      if (!r) return;
      const key = r.cellKey ?? buildCellKey(r.engine as ConversationEngine, r.language, r.zone);
      m.set(key, r);
      // Index alignment is the endpoint's contract; keep it as a fallback key.
      m.set(`#idx${i}`, r);
    });
    return m;
  }, [rows]);

  const zoneRows = useMemo(() => {
    const zones = new Map<string, ConversationPersona['cells']>();
    for (const c of persona.cells) {
      const key = `${c.zone}|${c.language}`;
      if (!zones.has(key)) zones.set(key, []);
      zones.get(key)!.push(c);
    }
    return [...zones.entries()];
  }, [persona.cells]);

  const engines = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const c of persona.cells) if (!seen.has(c.engine)) { seen.add(c.engine); list.push(c.engine); }
    return list;
  }, [persona.cells]);

  const openRun = (cell: ConversationSubRun, runIdx: number) => {
    const cp = buildCellPath(cell.engine as ConversationEngine, cell.language, cell.zone);
    navigate(`/brand/conversations/${persona.personaId}/launches/${launchId}/cells/${cp}/runs/${runIdx}`);
  };

  if (loading) return <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />;

  const pendingLabel = firstRunAt
    ? t('conversations.personaPendingZone', {
        date: new Date(firstRunAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }),
      })
    : t('conversations.persona.cellNoRunYet');

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 160 + engines.length * 180 }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={HEAD_SX}>{t('conversations.persona.matrixZoneCol')}</Box>
              {engines.map((eng) => (
                <Box component="th" key={eng} sx={HEAD_SX}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, textTransform: 'none', letterSpacing: 0, fontSize: '0.8125rem', color: 'text.primary' }}>
                    <EngineLogo engine={engineLogoId(eng)} size={16} />
                    {engineDisplay(eng)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {zoneRows.map(([zoneKey, zoneCells]) => {
              const [zone, lang] = zoneKey.split('|');
              const meta = zoneCells[0]?.zoneMeta;
              return (
                <Box component="tr" key={zoneKey}>
                  <Box component="td" sx={CELL_SX}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      {meta?.countryCode && (
                        <span style={{ fontSize: '1.1em', lineHeight: 1 }}>
                          {[...meta.countryCode.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0))).join('')}
                        </span>
                      )}
                      <Typography variant="body2" fontWeight={600} component="span">{meta?.label ?? zone}</Typography>
                      <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.6875rem', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: '3px', px: '4px', lineHeight: 1.6 }}>
                        {lang}
                      </Box>
                    </Box>
                  </Box>

                  {engines.map((eng) => {
                    const configured = persona.cells.find(
                      (c) => c.engine === eng && c.zone === zone && c.language === lang,
                    );
                    if (!configured) {
                      return (
                        <Box component="td" key={eng} sx={{ ...CELL_SX, ...HATCH_SX, color: 'text.disabled' }}>
                          <Typography variant="caption" color="text.disabled">
                            {t('conversations.persona.cellUntracked')}
                          </Typography>
                        </Box>
                      );
                    }

                    const key = buildCellKey(configured.engine, configured.language, configured.zone);
                    const idx = persona.cells.indexOf(configured);
                    const row = byKey.get(key) ?? byKey.get(`#idx${idx}`) ?? null;

                    if (!row) {
                      return (
                        <Box component="td" key={eng} sx={{ ...CELL_SX, ...HATCH_SX }}>
                          <Typography variant="caption" color="text.disabled">{pendingLabel}</Typography>
                        </Box>
                      );
                    }

                    // Read the status BEFORE the guard: the negative branch of a
                    // `cell is ConversationSubRun` predicate narrows `row` to `null`.
                    const rowStatus = row.status;
                    if (!isFinalized(row)) {
                      return (
                        <Box component="td" key={eng} sx={CELL_SX}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} thickness={5} />
                            <Typography variant="caption" color="text.secondary">
                              {TERMINAL_STATUSES.has(rowStatus)
                                ? t('conversations.progressFinalizing')
                                : t('conversations.persona.cellRunning')}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }

                    if (row.status === 'failed') {
                      return (
                        <Box component="td" key={eng} sx={CELL_SX}>
                          <Typography variant="body2" fontWeight={700} color="text.secondary">
                            {t('conversations.cellFailed')}
                          </Typography>
                        </Box>
                      );
                    }

                    const n = row.stats?.n ?? 0;
                    const won = n > 0 ? (row.stats?.cReco ?? 0) / n : null;
                    const runs = Array.isArray(row.runs) ? [...row.runs].sort((a, b) => a.idx - b.idx) : [];

                    return (
                      <Box component="td" key={eng} sx={CELL_SX}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          {t('conversations.persona.cellAvg', {
                            presence: pct(row.presence) ?? t('conversations.persona.inProgress'),
                            won: pct(won) ?? t('conversations.persona.inProgress'),
                          })}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {runs.map((r) => (
                            <RunSquare
                              key={r.idx}
                              run={r}
                              label={`${t('conversations.transcriptRun', { n: r.idx + 1 })} — ${squareDesc(r, t)}`}
                              onClick={() => openRun(row, r.idx)}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* The squares must never rely on colour alone (§4.5.3) */}
      <Box sx={{ mt: 2, pt: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
        <RunSquareLegend />
      </Box>
    </Box>
  );
}
